'use client';

import { logger } from '@adornis/base/logging.js';
import { ArrowLeft, Search, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getClassRoomByCodeQuery } from '../../../db/api/get-classroom-by-code.js';
import { getClassRoomsByIdsQuery } from '../../../db/api/get-classrooms-by-ids.js';
import { getMyClassMembershipsQuery } from '../../../db/api/get-my-class-memberships.js';
import { requestToJoinClassMutation } from '../../../db/api/request-to-join-class.js';
import type { ClassMembership, ClassMembershipStatus } from '../../../db/class-membership.js';
import type { ClassRoom } from '../../../db/classroom.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Input } from '../ui/input.js';

interface JoinClassPageProps {
  onBack: () => void;
  initialCode?: string | null;
  /** Called once after the initial-code lookup has been triggered, so the caller can clear its
   * stashed value and avoid re-triggering the lookup on a later, unrelated remount of this page. */
  onInitialCodeConsumed?: () => void;
}

const STATUS_STYLES: Record<ClassMembershipStatus, string> = {
  pending: 'bg-amber-500',
  approved: 'bg-green-600',
  rejected: 'bg-destructive',
};

export function JoinClassPage({ onBack, initialCode, onInitialCodeConsumed }: JoinClassPageProps) {
  const { t } = useTranslation();

  const [codeInput, setCodeInput] = useState(initialCode ?? '');
  const [lookupResult, setLookupResult] = useState<ClassRoom | null>(null);
  const [lookupAttempted, setLookupAttempted] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  const [memberships, setMemberships] = useState<ClassMembership[]>([]);
  const [classroomsByID, setClassroomsByID] = useState<Record<string, ClassRoom>>({});
  const [isLoadingMemberships, setIsLoadingMemberships] = useState(false);

  const loadMemberships = () => {
    setIsLoadingMemberships(true);
    void getMyClassMembershipsQuery()({ _id: 1, classRoomID: 1, status: 1, requestedAt: 1 })
      .then(rows => {
        setMemberships(rows ?? []);
        const ids = Array.from(new Set((rows ?? []).map(m => m.classRoomID)));
        if (ids.length === 0) return;
        return getClassRoomsByIdsQuery(ids)({ _id: 1, name: 1 }).then(classrooms => {
          const map: Record<string, ClassRoom> = {};
          for (const classroom of classrooms ?? []) map[classroom._id] = classroom;
          setClassroomsByID(map);
        });
      })
      .catch(err => logger.error(err, 'Failed to load class memberships'))
      .finally(() => setIsLoadingMemberships(false));
  };

  useEffect(() => {
    loadMemberships();
  }, []);

  const handleLookup = async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) return;

    setIsLookingUp(true);
    setJoinError(null);
    try {
      const results = await getClassRoomByCodeQuery(trimmed)({ _id: 1, name: 1 });
      setLookupResult(results?.[0] ?? null);
      setLookupAttempted(true);
    } catch (err) {
      logger.error(err, 'Failed to look up class by code');
      setLookupResult(null);
      setLookupAttempted(true);
    } finally {
      setIsLookingUp(false);
    }
  };

  useEffect(() => {
    if (initialCode) void handleLookup(initialCode);
    onInitialCodeConsumed?.();
    // Only run once on mount for the deep-link/pending-code handoff.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleJoin = async () => {
    if (!lookupResult) return;

    setIsJoining(true);
    setJoinError(null);
    try {
      await requestToJoinClassMutation(codeInput.trim())({ _id: 1, status: 1 });
      setLookupResult(null);
      setLookupAttempted(false);
      setCodeInput('');
      loadMemberships();
    } catch (err) {
      logger.error(err, 'Failed to request to join class');
      setJoinError(t('JoinClass.alerts.joinError'));
    } finally {
      setIsJoining(false);
    }
  };

  const existingMembership = lookupResult ? memberships.find(m => m.classRoomID === lookupResult._id) : null;

  return (
    <div className="flex flex-col h-full w-full bg-background overflow-y-auto">
      <div className="sticky top-0 z-10 border-b border-border bg-card px-4 sm:px-8 py-2 sm:py-4 shadow-sm">
        <div className="flex items-center gap-2 max-w-4xl mx-auto">
          <Button
            onClick={onBack}
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground tracking-tight">{t('JoinClass.title')}</h1>
        </div>
      </div>

      <div className="flex-1 px-4 sm:px-8 py-8">
        <div className="max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500">
          <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-4">{t('JoinClass.enterCodeTitle')}</h2>
            <div className="flex items-center gap-2">
              <Input
                value={codeInput}
                onChange={e => {
                  setCodeInput(e.target.value.toUpperCase());
                  setLookupResult(null);
                  setLookupAttempted(false);
                }}
                placeholder={t('JoinClass.codePlaceholder')}
                className="font-mono tracking-widest max-w-[220px]"
                maxLength={6}
                onKeyDown={e => {
                  if (e.key === 'Enter') void handleLookup(codeInput);
                }}
              />
              <Button
                variant="outline"
                className="gap-1.5"
                onClick={() => void handleLookup(codeInput)}
                disabled={isLookingUp || !codeInput.trim()}
              >
                <Search className="h-4 w-4" /> {t('JoinClass.findButton')}
              </Button>
            </div>

            {lookupAttempted && !isLookingUp && (
              <div className="mt-4">
                {!lookupResult ? (
                  <p className="text-sm text-destructive">{t('JoinClass.codeNotFound')}</p>
                ) : existingMembership ? (
                  <div className="flex items-center gap-3 p-4 bg-background border border-border rounded-lg">
                    <p className="text-sm text-foreground">
                      {t('JoinClass.alreadyRequested', { className: lookupResult.name })}
                    </p>
                    <Badge className={STATUS_STYLES[existingMembership.status]}>
                      {t(`JoinClass.status.${existingMembership.status}`)}
                    </Badge>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3 p-4 bg-background border border-border rounded-lg">
                    <p className="text-sm text-foreground">
                      {t('JoinClass.confirmJoin', { className: lookupResult.name })}
                    </p>
                    <Button size="sm" className="gap-1.5" onClick={() => void handleJoin()} disabled={isJoining}>
                      <UserPlus className="h-3.5 w-3.5" /> {t('JoinClass.requestButton')}
                    </Button>
                  </div>
                )}
                {joinError && <p className="text-sm text-destructive mt-2">{joinError}</p>}
              </div>
            )}
          </section>

          <section className="bg-card rounded-xl p-6 sm:p-8 border border-border shadow-sm">
            <h2 className="text-lg font-semibold text-foreground mb-6">{t('JoinClass.myClassesTitle')}</h2>

            {isLoadingMemberships ? (
              <p className="text-sm text-muted-foreground">{t('JoinClass.loading')}</p>
            ) : memberships.length === 0 ? (
              <p className="text-sm italic text-muted-foreground/70">{t('JoinClass.noClasses')}</p>
            ) : (
              <div className="space-y-2">
                {memberships.map(membership => (
                  <div
                    key={membership._id}
                    className="flex items-center justify-between gap-3 p-4 bg-background border border-border rounded-lg"
                  >
                    <p className="font-medium text-foreground truncate">
                      {classroomsByID[membership.classRoomID]?.name ?? membership.classRoomID}
                    </p>
                    <Badge className={STATUS_STYLES[membership.status]}>
                      {t(`JoinClass.status.${membership.status}`)}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
